"use client";

import {
  getToken,
  isFirebaseMessagingSupported,
  messaging,
  onMessage,
} from "@/lib/firebase";
import { userService } from "@/services/api/user";
import type { MessagePayload } from "@firebase/messaging";
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";

const isNotificationSupported = () => {
  return typeof window !== "undefined" && "Notification" in window;
};

export const usePushNotifications = () => {
  const [token, setToken] = useState<string | null>(null);
  const [permissionStatus, setPermissionStatus] =
    useState<NotificationPermission>(
      isNotificationSupported() ? Notification.permission : "denied",
    );

  const isSupported =
    isNotificationSupported() && isFirebaseMessagingSupported();

  const retrieveToken = useCallback(async () => {
    if (!isSupported || !messaging) {
      console.warn("Firebase messaging not supported on this device");
      return;
    }

    try {
      const vapidKey = import.meta.env.VITE_FIREBASE_VAPID_KEY;
      const currentToken = await getToken(messaging, { vapidKey });
      if (currentToken) {
        setToken(currentToken);
        setPermissionStatus("granted");
        await userService.registerPushToken(currentToken);
      }
    } catch (error) {
      console.error("Error retrieving FCM token:", error);
    }
  }, [isSupported]);

  const requestPermission = useCallback(async () => {
    if (!isSupported) {
      toast.error("Notificações push não são suportadas neste dispositivo.");
      return;
    }

    try {
      const permission = await Notification.requestPermission();
      setPermissionStatus(permission);
      if (permission === "granted") {
        await retrieveToken();
        toast.success("Notificações habilitadas!");
      } else {
        toast.error("Notificações desabilitadas.");
      }
    } catch (error) {
      console.error("Error requesting notification permission:", error);
    }
  }, [retrieveToken, isSupported]);

  const setupForegroundMessages = useCallback(() => {
    if (!isSupported || !messaging) {
      return;
    }

    onMessage(messaging, (payload: MessagePayload) => {
      if (payload.notification) {
        toast.info(payload.notification.title || "Nova notificação", {
          description: payload.notification.body,
          closeButton: true,
          position: "bottom-right",
        });
      }
    });
  }, [isSupported]);

  useEffect(() => {
    if (isSupported && Notification.permission === "granted") {
      retrieveToken();
    }
  }, [retrieveToken, isSupported]);

  return {
    token,
    permissionStatus,
    requestPermission,
    setupForegroundMessages,
    isSupported,
  };
};
